/**
 * The one gate every file entering Bower passes through. Pure: it returns
 * what to keep, what to refuse and why, and how many were left out, and the
 * caller (the intake hook) says so. Too many files is handled the same way
 * everywhere (decision D5): keep the first `maxFiles` that pass and count the
 * rest, never refuse the whole batch.
 */

import { MAX_UPLOAD_BYTES } from '@bower/shared/schemas/upload.schema';

import type { IntakeLimits, IntakeRejection, IntakeResult, IntakeRule } from './types';

export const formatMegabytes = (bytes: number) => `${Math.round(bytes / 1024 / 1024)} MB`;

export function validateFiles(
  files: readonly File[],
  rule: IntakeRule,
  { maxFiles, maxBytes = MAX_UPLOAD_BYTES }: IntakeLimits,
): IntakeResult {
  const passed: File[] = [];
  const rejected: IntakeRejection[] = [];

  for (const file of files) {
    const reason =
      file.size > maxBytes ? `over the ${formatMegabytes(maxBytes)} limit` : rule.test(file);
    if (reason) rejected.push({ file, reason });
    else passed.push(file);
  }

  const kept = Math.max(0, maxFiles);
  return {
    accepted: passed.slice(0, kept),
    rejected,
    skipped: Math.max(0, passed.length - kept),
  };
}

/** Reasons shown in full before the summary counts the remainder. */
const MAX_SHOWN_REJECTIONS = 3;

/**
 * One line for a set of rejections, bounded: a recursive folder drop can
 * reject hundreds, and joining them all once put 14.7k characters in a toast.
 */
export function summarizeRejections(rejected: readonly IntakeRejection[]): string {
  const shown = rejected
    .slice(0, MAX_SHOWN_REJECTIONS)
    .map(({ file, reason }) => `${file.name} (${reason})`)
    .join('; ');
  const hidden = rejected.length - MAX_SHOWN_REJECTIONS;
  return hidden > 0 ? `${shown}, and ${hidden} more` : shown;
}
